import mixpanel from 'mixpanel-browser';

let isInitialized = false;

/**
 * Initialize Mixpanel with the project token from environment variables
 */
export const initMixpanel = () => {
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;
  
  if (!token || token === "undefined") {
    console.warn("[mixpanel] REACT_APP_MIXPANEL_TOKEN not set, Mixpanel will not be initialized");
    return;
  }

  try {
    mixpanel.init(token, {
      debug: process.env.NODE_ENV === 'development',
      track_pageview: false, // We'll track page views manually
      persistence: 'localStorage'
    });
    isInitialized = true;
    console.log("[mixpanel] Initialized successfully");
  } catch (error) {
    console.error("[mixpanel] Initialization error", error);
  }
};

/**
 * Track an event with Mixpanel
 * @param {string} eventName - Name of the event to track
 * @param {object} properties - Event properties (e.g., URL, UTM parameters)
 */
export const trackEvent = (eventName, properties = {}) => {
  if (!isInitialized) {
    console.warn("[mixpanel] Not initialized, skipping event:", eventName);
    return;
  }

  try {
    mixpanel.track(eventName, properties);
    console.log("[mixpanel] Tracked event:", eventName, properties);
  } catch (error) {
    console.error("[mixpanel] Error tracking event:", error);
  }
};

/**
 * Generate a unique tracking ID for conversion tracking
 * @returns {string} Unique tracking ID (UUID v4 format)
 */
export const generateTrackingId = () => {
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Parse UTM parameters from URL search string
 * @param {string} searchString - URL search string (e.g., "utm_source=google&utm_medium=cpc")
 * @returns {object} Object with UTM parameters as key-value pairs
 */
export const parseUTMParameters = (searchString) => {
  const params = {};
  if (!searchString) return params;

  const searchParams = new URLSearchParams(searchString);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      params[key] = value;
    }
  });

  return params;
};

/**
 * Add tracking ID to URL as query parameter
 * @param {string} url - Base URL
 * @param {string} trackingId - Tracking ID to add
 * @returns {string} URL with tracking_id parameter
 */
export const addTrackingIdToUrl = (url, trackingId) => {
  if (!trackingId) return url;
  
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set('tracking_id', trackingId);
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, append manually
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}tracking_id=${encodeURIComponent(trackingId)}`;
  }
};
